import { useEffect, useRef, useState } from 'react';

/**
 * Google AdSense Ad Unit Component
 * Only renders on content-rich pages (not on transactional/dashboard pages)
 * Fails gracefully if AdSense is blocked
 * 
 * @param {string} slot - Ad slot ID (optional, for specific ad units)
 * @param {string} format - Ad format: 'auto', 'rectangle', 'horizontal', 'vertical'
 * @param {string} className - Additional CSS classes
 */
export function AdUnit({ slot, format = 'auto', className = '' }) {
  const adRef = useRef(null);
  const isLoaded = useRef(false);
  const [adBlocked, setAdBlocked] = useState(false);

  useEffect(() => {
    // Only load ad once
    if (isLoaded.current) return;
    
    // Check if ads are likely blocked
    const checkAdBlock = () => {
      // If adsbygoogle is not defined after a short delay, ads are blocked
      setTimeout(() => {
        if (!window.adsbygoogle) {
          setAdBlocked(true);
        }
      }, 1000);
    };
    
    try {
      // Check if adsbygoogle is available
      if (window.adsbygoogle && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isLoaded.current = true;
      } else {
        checkAdBlock();
      }
    } catch (error) {
      // AdSense not loaded or blocked - fail silently
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.log('AdSense not available');
      }
      setAdBlocked(true);
    }
  }, []);

  // Don't render if page has noindex (transactional pages)
  const metaRobots = document.querySelector('meta[name="robots"]');
  if (metaRobots && metaRobots.content.includes('noindex')) {
    return null;
  }

  // If ads are blocked, render nothing (graceful failure)
  if (adBlocked) {
    return null;
  }

  const getFormatStyle = () => {
    switch (format) {
      case 'rectangle':
        return { display: 'inline-block', width: '300px', height: '250px' };
      case 'horizontal':
        return { display: 'block', width: '100%', height: '90px' };
      case 'vertical':
        return { display: 'inline-block', width: '160px', height: '600px' };
      default:
        return { display: 'block' };
    }
  };

  return (
    <div className={`ad-container ${className}`} data-testid="ad-unit">
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={getFormatStyle()}
        data-ad-client="ca-pub-7463683405264608"
        data-ad-slot={slot || ''}
        data-ad-format={format === 'auto' ? 'auto' : undefined}
        data-full-width-responsive={format === 'auto' ? 'true' : undefined}
      />
    </div>
  );
}

/**
 * Horizontal banner ad - good for between sections
 */
export function AdBanner({ className = '' }) {
  return (
    <div className={`my-8 ${className}`}>
      <AdUnit format="auto" />
    </div>
  );
}

/**
 * Sidebar ad unit - good for sidebars
 */
export function AdSidebar({ className = '' }) {
  return (
    <div className={`${className}`}>
      <AdUnit format="rectangle" />
    </div>
  );
}

export default AdUnit;
